import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { useMarket } from '../../context/MarketContext';
import { searchProducts } from '../../services/productService';
import { getBrandsAlphabetically } from '../../services/brandService';
import { toHandle } from '../../utils/handle';
import { formatPrice } from '../../utils/formatPrice';

const PROTECTED_PATHS = ['/account'];


const NAV_LINKS = [
  { label: 'Home',         to: '/' },
  { label: 'Shop All',     to: '/shop' },
  { label: 'Brands',       to: '/brands', hasMega: true },
  { label: 'New Arrivals', to: '/collections/new-arrivals' },
  { label: 'Contact',      to: '/contact' },
  { label: 'Track Order',  to: '/track-order' },
];

/* ── Icons ── */
const SearchIcon = ({ cls = 'w-5 h-5' }) => (
  <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
  </svg>
);
const HeartIcon = ({ filled, cls = 'w-5 h-5' }) => (
  <svg className={cls} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
  </svg>
);
const UserIcon = ({ cls = 'w-5 h-5' }) => (
  <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
  </svg>
);
const BagIcon = ({ cls = 'w-5 h-5' }) => (
  <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 10H4L5 9z"/>
  </svg>
);

/* ── Brands mega-menu ── */
const BrandsMega = ({ onClose }) => {
  const [grouped, setGrouped] = useState({});
  const letters = Object.keys(grouped).sort();
  const [query, setQuery] = useState('');

  useEffect(() => {
    getBrandsAlphabetically().then(setGrouped);
  }, []);

  const filtered = query
    ? Object.fromEntries(
        letters
          .map(l => [l, grouped[l].filter(b => b.name.toLowerCase().includes(query.toLowerCase()))])
          .filter(([, arr]) => arr.length > 0)
      )
    : Object.fromEntries(letters.map(l => [l, grouped[l]]));

  const filteredLetters = Object.keys(filtered).sort();

  return (
    <div className="absolute top-full left-0 mt-2 w-[520px] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-fade-in">
      {/* Top search bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search brands..."
            className="w-full bg-gray-50 rounded-lg px-3 py-2 text-sm border border-gray-200
                       focus:border-[#5B21B6] focus:outline-none focus:bg-white transition-all pr-8"
            autoFocus
          />
          <SearchIcon cls="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
        <Link to="/brands" onClick={onClose} className="text-sm font-medium text-[#5B21B6] hover:underline whitespace-nowrap ml-3">
          All Brands →
        </Link>
      </div>

      {/* Scrollable brand list */}
      <div className="max-h-[360px] overflow-y-auto px-4 py-3">
        {filteredLetters.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No brands found</p>
        ) : (
          <div className="space-y-1">
            {filteredLetters.map(letter => (
              <div key={letter}>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-3 mb-1 first:mt-0">
                  {letter}
                </p>
                {filtered[letter].length >= 3 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {filtered[letter].map(brand => (
                      <Link
                        key={brand.id}
                        to={`/collections/${brand.slug || toHandle(brand.name)}`}
                        onClick={onClose}
                        className="group flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#F5F3FF] transition-all"
                      >
                        <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-[#5B21B6] shrink-0">
                          {brand.name.slice(0, 3).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-[#5B21B6] whitespace-nowrap">
                          {brand.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  filtered[letter].map(brand => (
                    <Link
                      key={brand.id}
                      to={`/collections/${brand.slug || toHandle(brand.name)}`}
                      onClick={onClose}
                      className="group flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#F5F3FF] cursor-pointer transition-all"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-[#5B21B6] shrink-0">
                        {brand.name.slice(0, 3).toUpperCase()}
                      </div>
                      <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-[#5B21B6]">
                        {brand.name}
                      </span>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-[#5B21B6] ml-auto shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </Link>
                  ))
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Header = () => {
  const { itemCount, setIsDrawerOpen } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const { user, logout } = useAuth();
  const { currentMarket } = useMarket();
  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;
  const navigate   = useNavigate();
  const location   = useLocation();

  const handleLogout = useCallback(async () => {
    await logout();
    toast.success('Signed out successfully');
    const isProtected = PROTECTED_PATHS.some(p => location.pathname.startsWith(p));
    if (isProtected) navigate('/', { replace: true });
  }, [logout, location.pathname, navigate]);

  const [searchQuery,      setSearchQuery]      = useState('');
  const [searchResults,    setSearchResults]    = useState([]);
  const [showSearch,       setShowSearch]       = useState(false);
  const [searchLoading,    setSearchLoading]    = useState(false);
  const [mobileOpen,       setMobileOpen]       = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [userOpen,         setUserOpen]         = useState(false);
  const [brandsMega,       setBrandsMega]       = useState(false);
  const brandsCloseTimer = useRef(null);
  const [scrolled,      setScrolled]      = useState(false);
  const [cartBounce,    setCartBounce]    = useState(false);

  const searchRef          = useRef(null);
  const mobileSearchRef    = useRef(null);
  const brandsRef          = useRef(null);
  const prevCount          = useRef(itemCount);
  const searchDebounceRef  = useRef(null);

  /* Sticky shadow */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 56);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* Cart bounce */
  useEffect(() => {
    if (itemCount > prevCount.current) {
      setCartBounce(true);
      const t = setTimeout(() => setCartBounce(false), 500);
      return () => clearTimeout(t);
    }
    prevCount.current = itemCount;
  }, [itemCount]);

  /* Close search on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* Close brands mega on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (brandsRef.current && !brandsRef.current.contains(e.target)) setBrandsMega(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* Close mobile search on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) setMobileSearchOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* Lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    clearTimeout(searchDebounceRef.current);
    if (q.length < 2) { setShowSearch(false); setSearchResults([]); setSearchLoading(false); return; }
    setShowSearch(true);
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await searchProducts(q, marketCode);
        setSearchResults((results || []).slice(0, 6));
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false); setSearchQuery(''); setMobileOpen(false);
    }
  };

  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 bg-white transition-all duration-300 border-b border-border
          ${scrolled ? 'shadow-[0_2px_12px_rgba(0,0,0,0.08)]' : ''}`}
      >
        {/* Top row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">

          {/* Logo */}
          <Link to="/" className="shrink-0" aria-label="Skinn Thrive Home">
            <img
              src="/favicon-96x96.png"
              alt="Skinn Thrive"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Search bar — desktop */}
          <div ref={searchRef} className="hidden md:flex flex-1 max-w-lg mx-6 relative">
            <form onSubmit={handleSearchSubmit} className="w-full">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Search products, brands, skin concerns..."
                  className="w-full border border-border rounded-lg px-4 py-2.5 pr-11 text-sm bg-surface text-ink
                             placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <button type="submit" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-primary transition-colors">
                  <SearchIcon />
                </button>
              </div>
            </form>

            {/* Search dropdown */}
            {showSearch && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-card-hover border border-border z-50 overflow-hidden animate-fade-in">
                {searchLoading ? (
                  <div className="flex items-center justify-center gap-2 py-5 text-sm text-ink-soft">
                    <svg className="animate-spin w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Searching…
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.map(p => (
                      <Link
                        key={p.id}
                        to={`/product/${p.handle}`}
                        onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-primary-light transition-colors group"
                      >
                        <div className="w-11 h-11 rounded-lg overflow-hidden bg-surface shrink-0 border border-border">
                          <img src={p.images[0]} alt={p.title} className="w-full h-full object-contain p-0.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-ink truncate group-hover:text-primary">{p.title}</p>
                          <p className="text-xs text-ink-soft mt-0.5">{p.vendor} · <span className="font-semibold text-primary">{formatPrice(p.price)}</span></p>
                        </div>
                      </Link>
                    ))}
                    <button
                      onClick={handleSearchSubmit}
                      className="w-full text-center text-sm font-semibold text-primary py-3 hover:bg-primary-light border-t border-border transition-colors"
                    >
                      See all results for "{searchQuery}" →
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-ink-muted text-center py-5">No products found for "{searchQuery}"</p>
                )}
              </div>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-0.5 ml-auto">

            {/* Mobile search */}
            <button
              className="md:hidden p-2.5 text-ink-soft hover:text-primary hover:bg-primary-light rounded-lg transition-all"
              onClick={() => { setMobileSearchOpen(o => !o); setSearchQuery(''); setSearchResults([]); }}
              aria-label="Search"
            >
              <SearchIcon cls="w-5 h-5" />
            </button>

            {/* Wishlist — hidden on mobile (bottom nav handles it) */}
            <Link
              to="/wishlist"
              className="hidden md:flex relative p-2.5 text-ink-soft hover:text-accent hover:bg-accent-light rounded-lg transition-all items-center justify-center"
              aria-label={`Wishlist (${wishlistCount})`}
            >
              <HeartIcon filled={wishlistCount > 0} cls={`w-5 h-5 ${wishlistCount > 0 ? 'text-accent' : ''}`} />
              {wishlistCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 text-[10px] font-bold bg-accent text-white rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* User */}
            <div className="hidden md:block relative">
              <button
                onClick={() => setUserOpen(!userOpen)}
                className="p-2.5 text-ink-soft hover:text-primary hover:bg-primary-light rounded-lg transition-all"
                aria-label="Account"
              >
                <UserIcon cls="w-5 h-5" />
              </button>

              {userOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-card-hover border border-border w-52 z-50 overflow-hidden animate-fade-in">
                    {user ? (
                      <>
                        <div className="px-4 py-3.5 bg-primary-light border-b border-border">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold shrink-0">
                              {user.name?.[0]?.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-ink truncate">{user.name}</p>
                              <p className="text-xs text-ink-soft truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>
                        {[['My Account', '/account'], ['My Orders', '/account/orders'], ['Wishlist', '/wishlist']].map(([l, t]) => (
                          <Link key={t} to={t} onClick={() => setUserOpen(false)}
                            className="flex items-center px-4 py-2.5 text-sm text-ink-soft hover:text-primary hover:bg-primary-light transition-colors">
                            {l}
                          </Link>
                        ))}
                        <button
                          onClick={() => { handleLogout(); setUserOpen(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 border-t border-border transition-colors"
                        >
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="p-4">
                          <p className="text-sm font-semibold text-ink mb-1">Welcome!</p>
                          <p className="text-xs text-ink-soft mb-3">Sign in to view orders & wishlist</p>
                          <Link to="/login" onClick={() => setUserOpen(false)} className="btn-primary w-full btn-sm justify-center">
                            Sign In
                          </Link>
                        </div>
                        <div className="px-4 pb-3 border-t border-border pt-3">
                          <Link to="/register" onClick={() => setUserOpen(false)} className="text-sm text-primary font-medium hover:underline">
                            Create account →
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Cart */}
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="relative p-2.5 text-ink-soft hover:text-primary hover:bg-primary-light rounded-lg transition-all"
              aria-label={`Cart (${itemCount} items)`}
            >
              <BagIcon cls={`w-5 h-5 ${cartBounce ? 'animate-cart-bounce text-primary' : ''}`} />
              {itemCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 text-[10px] font-bold bg-primary text-white rounded-full flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>

            {/* Hamburger — mobile */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2.5 text-ink-soft hover:text-primary hover:bg-primary-light rounded-lg transition-all ml-0.5"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:block border-t border-border bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex items-center gap-1 h-11">
              {NAV_LINKS.map(({ label, to, hasMega }) => (
                <li key={to} className="relative" ref={hasMega ? brandsRef : undefined}>
                  {hasMega ? (
                    <>
                      <button
                        onMouseEnter={() => {
                          if (brandsCloseTimer.current) clearTimeout(brandsCloseTimer.current);
                          setBrandsMega(true);
                        }}
                        onMouseLeave={() => {
                          brandsCloseTimer.current = setTimeout(() => setBrandsMega(false), 150);
                        }}
                        onClick={() => setBrandsMega(!brandsMega)}
                        className={`nav-link px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1
                          ${isActive(to) ? 'text-primary bg-primary-light' : ''}`}
                      >
                        {label}
                        <svg className={`w-3.5 h-3.5 transition-transform ${brandsMega ? 'rotate-180' : ''}`}
                             fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      {brandsMega && (
                        <div
                          onMouseEnter={() => {
                            if (brandsCloseTimer.current) clearTimeout(brandsCloseTimer.current);
                          }}
                          onMouseLeave={() => {
                            brandsCloseTimer.current = setTimeout(() => setBrandsMega(false), 150);
                          }}
                        >
                          <BrandsMega onClose={() => setBrandsMega(false)} />
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={to}
                      className={`nav-link px-4 py-1.5 rounded-lg text-sm font-medium
                        ${isActive(to) ? 'text-primary bg-primary-light' : ''}`}
                    >
                      {label}
                    </Link>
                  )}
                </li>
              ))}
              {/* Promo pill */}
              <li className="ml-auto">
                <span className="inline-flex items-center gap-1.5 bg-accent/10 text-accent text-xs font-semibold px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  Use SAVE10 · 10% off
                </span>
              </li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Mobile search overlay */}
      {mobileSearchOpen && (
        <div ref={mobileSearchRef} className="md:hidden fixed top-16 inset-x-0 z-50 bg-white border-b border-border shadow-lg animate-fade-in">
          <div className="px-4 py-3">
            <form onSubmit={(e) => { handleSearchSubmit(e); setMobileSearchOpen(false); }}>
              <div className="relative">
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Search products, brands, skin concerns..."
                  className="w-full border border-border rounded-xl px-4 py-3 pr-10 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-primary transition-colors">
                  <SearchIcon cls="w-4 h-4" />
                </button>
              </div>
            </form>

            {searchQuery.length >= 2 && (
              <div className="mt-2 rounded-xl border border-border overflow-hidden">
                {searchLoading ? (
                  <div className="flex items-center justify-center gap-2 py-5 text-sm text-ink-soft">
                    <svg className="animate-spin w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Searching…
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    {searchResults.map(p => (
                      <Link
                        key={p.id}
                        to={`/product/${p.handle}`}
                        onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); setSearchResults([]); }}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-primary-light transition-colors border-b border-border last:border-0"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-surface shrink-0 border border-border">
                          <img src={p.images[0]} alt={p.title} className="w-full h-full object-contain p-0.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-ink truncate">{p.title}</p>
                          <p className="text-xs text-ink-soft">{p.vendor} · <span className="font-semibold text-primary">{formatPrice(p.price)}</span></p>
                        </div>
                      </Link>
                    ))}
                    <button
                      onClick={() => { handleSearchSubmit(); setMobileSearchOpen(false); }}
                      className="w-full text-sm font-semibold text-primary py-3 hover:bg-primary-light transition-colors"
                    >
                      See all results for "{searchQuery}" →
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-ink-muted text-center py-4">No products found for "{searchQuery}"</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile full-screen menu */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm animate-fade-in" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-4/5 max-w-xs bg-white z-[70] flex flex-col animate-slide-in-left shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <img
                src="/favicon-96x96.png"
                alt="Skinn Thrive"
                className="h-8 w-auto object-contain"
              />
              <button onClick={() => setMobileOpen(false)} className="p-2 text-ink-soft hover:text-primary hover:bg-primary-light rounded-lg transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="px-4 py-3 border-b border-border">
              <form onSubmit={(e) => { handleSearchSubmit(e); setMobileOpen(false); }}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder="Search products..."
                    className="w-full border border-border rounded-lg px-4 py-2.5 pr-10 text-sm bg-surface focus:outline-none focus:border-primary"
                  />
                  <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted">
                    <SearchIcon cls="w-4 h-4" />
                  </button>
                </div>
              </form>
              {searchResults.length > 0 && (
                <div className="mt-2 rounded-xl border border-border overflow-hidden">
                  {searchResults.slice(0, 4).map(p => (
                    <Link
                      key={p.id}
                      to={`/product/${p.handle}`}
                      onClick={() => { setMobileOpen(false); setSearchQuery(''); setSearchResults([]); }}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-primary-light transition-colors border-b border-border last:border-0"
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-surface shrink-0 border border-border">
                        <img src={p.images[0]} alt={p.title} className="w-full h-full object-contain p-0.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-ink truncate">{p.title}</p>
                        <p className="text-[11px] text-ink-soft">{formatPrice(p.price)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <nav className="flex-1 overflow-y-auto py-2">
              {NAV_LINKS.map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center px-5 py-3.5 text-sm font-medium border-b border-gray-50 last:border-0 transition-colors
                    ${isActive(to) ? 'text-primary bg-primary-light' : 'text-ink-soft hover:text-primary hover:bg-gray-50'}`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            <div className="px-5 py-4 border-t border-border bg-surface">
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white font-bold shrink-0">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{user.name}</p>
                    <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="text-xs text-red-500 hover:underline">Sign out</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-primary btn-sm flex-1 justify-center">Sign In</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="btn-outline btn-sm flex-1 justify-center">Register</Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
